import {
  ElementType,
  FunctionComponent,
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import sha1 from 'sha1';
import { ConfigContext, DetailsModalContext, UserContext } from '../../../../context';
import {
  StoryCardArticleContent,
  StoryCardContentArticlePost,
  StoryCardContentArticleQuote,
  StoryCardContentHeadline,
  StoryCardType,
} from '../../../../services';
import {
  ActionPane,
  AuthorInfo,
  CardActionsProps,
  CardOpenButton,
  CARDS_TO_BLOCK,
  Comments,
  FeedCardProps,
  FeedCardTags,
  FeedCardTagsVariant,
  getStoryCardName,
  layoutPath,
  ReactionsCounter,
  truncateString,
  useAnalytics,
  useCardDate,
  useFreeLoginPopup,
} from '../../../../shared';
import { FeedCardMargin } from '../FeedCardMargin';

import classNames from 'classnames';
import classes from './FeedCardWrapperCommon.module.scss';

interface FeedCardWrapperCommonProps extends FeedCardProps {
  CardActionsComponent: ElementType<CardActionsProps>;
  authorTimeSyncComponent?: JSX.Element;
}

export const FeedCardWrapperCommon: FunctionComponent<
  PropsWithChildren<FeedCardWrapperCommonProps>
> = memo(
  ({
    card,
    children,
    CardActionsComponent,
    authorTimeSyncComponent,
    isProfileFeedCard,
    chatId,
  }) => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

    const { logClickOpenArticle } = useAnalytics();

    const { showPostedTime: cardShowPostedTime = true } =
      useContext(ConfigContext).config.elements.card;

    const { userInfo, userProfile } = useContext(UserContext);

    const { id: userId, channelId } = userInfo.userData;

    const { id: userProfileId, isAnonymous } = userProfile;

    const { setDetailsModalData } = useContext(DetailsModalContext);

    const {
      id: cardId,
      type,
      story: { title: storyTitle },
      storyId,
      reactions,
      comments,
      options: { allowDisplayAuthor, allowCardReaction, allowComment },
      commentsRootCount,
      commentsCount,
      author,
      dateToDisplay,
      myReaction,
      content,
      externalTags,
      externalStatusTags,
    } = card;

    const date = useCardDate(dateToDisplay);

    const { id: authorId, avatar, screenName: authorName } = { ...author };

    const {
      styles: { backgroundColor },
    } = content;

    const { headline } = content as StoryCardContentHeadline;

    const { url } = content as StoryCardContentArticleQuote;

    const { urlFormatted } = content as StoryCardArticleContent;

    const { contentAuthor } = content as StoryCardContentArticlePost;

    const isAuthor = useMemo(() => Boolean(authorId === userProfileId), [authorId, userProfileId]);

    const isArticleOrPost = useMemo(
      () => [StoryCardType.ARTICLE, StoryCardType.POST].includes(type),
      [type]
    );

    const showPostedTime = useMemo(
      () => cardShowPostedTime && date && (!allowDisplayAuthor || Boolean(chatId)),
      [allowDisplayAuthor, cardShowPostedTime, chatId, date]
    );

    const totalReactionsCount = useMemo(
      () =>
        Object.values(reactions)
          .filter(({ count }) => Boolean(count))
          .reduce((res, { count }) => res + count, 0) as number,
      [reactions]
    );

    const clickableChild = useMemo(
      () => [StoryCardType.THREAD, StoryCardType.ARTICLE, StoryCardType.POST].includes(type),
      [type]
    );

    const onlyMyReaction = useMemo(
      () => Boolean(myReaction && totalReactionsCount === 1),
      [myReaction, totalReactionsCount]
    );

    const storyTitleTrunc = useMemo(
      () => truncateString({ value: storyTitle, maxLength: 100 }),
      [storyTitle]
    );

    const headerSubTitle = useMemo(
      () =>
        `${t('feed.posted-in')} "${storyTitleTrunc}"${
          cardShowPostedTime ? `${t('common.separator')} ${date}` : ''
        }`,
      [cardShowPostedTime, date, storyTitleTrunc, t]
    );

    const showCardComment = useMemo(
      () => Boolean(headline) && (type === StoryCardType.QUOTE || isArticleOrPost),
      [headline, isArticleOrPost, type]
    );

    const renderReactionsCounter = useMemo(
      () => Boolean(allowCardReaction && totalReactionsCount > 0 && !onlyMyReaction),
      [allowCardReaction, onlyMyReaction, totalReactionsCount]
    );

    const authorOrPosted = useMemo(
      () => Boolean(contentAuthor || showPostedTime),
      [contentAuthor, showPostedTime]
    );

    const commentsCounterClick = useCallback(() => {
      if (isMobileOnly) {
        navigate(
          layoutPath(`/details/${storyId}/${cardId}?scrollToComments=true&activeCommentsInput=true`)
        );
        return;
      }

      setDetailsModalData({ storyId, cardId, scrollToComments: true, activeCommentsInput: true });
    }, [cardId, navigate, setDetailsModalData, storyId]);

    const childClickHandler = useCallback(() => {
      if (
        CARDS_TO_BLOCK.find(
          (cardToBlock) => cardToBlock.storyId === storyId && cardToBlock.cardId === cardId
        )
      ) {
        toast.info(t('common.blocked-card'));
        return;
      }

      if (url?.includes(`/apps/posts/${channelId}/`)) {
        const [storyId, cardId] = url.split(`/apps/posts/${channelId}/`)[1].split('/');

        isMobileOnly
          ? navigate(layoutPath(`/details/${storyId}/${cardId}`))
          : setDetailsModalData({ storyId: parseInt(storyId, 10), cardId: parseInt(cardId, 10) });

        return;
      }

      if ([StoryCardType.ARTICLE, StoryCardType.QUOTE].includes(type)) {
        if (isAnonymous && urlFormatted?.includes('www.riddle.com')) {
          freeLoginHandler();
          return;
        }

        logClickOpenArticle({
          user_Id: sha1(`${userId}`),
          channel_id: channelId,
          story_id: storyId,
          item_id: cardId,
          time_stamp: new Date().toISOString(),
        });

        window.open(urlFormatted ?? url, '_blank', 'noreferrer');
        return;
      }

      isMobileOnly
        ? navigate(layoutPath(`/details/${storyId}/${cardId}`))
        : setDetailsModalData({ storyId, cardId, chatId });
    }, [
      cardId,
      channelId,
      chatId,
      freeLoginHandler,
      isAnonymous,
      logClickOpenArticle,
      navigate,
      setDetailsModalData,
      storyId,
      t,
      type,
      url,
      urlFormatted,
      userId,
    ]);

    const cardHeaderClickHandler = useCallback(() => {
      navigate(layoutPath(`/profile${!isAuthor ? `/${authorId}` : ''}`));
      window.scrollTo(0, 0);
    }, [authorId, isAuthor, navigate]);

    const cardCommentDetails = useCallback(() => {
      isMobileOnly
        ? navigate(layoutPath(`/details/${storyId}/${cardId}?scrollToComments=true`))
        : setDetailsModalData({ storyId, cardId, scrollToComments: true });
    }, [cardId, navigate, setDetailsModalData, storyId]);

    return (
      <>
        <div
          className={classNames(`card-${cardId}`, classes['card-wrapper'])}
          style={{ ...(!showCardComment && { backgroundColor }) }}
        >
          {!chatId && allowDisplayAuthor && (
            <FeedCardMargin top right bottom left>
              <AuthorInfo
                avatarUrl={avatar?.url}
                title={authorName}
                subTitle={headerSubTitle}
                onClick={cardHeaderClickHandler}
              />
            </FeedCardMargin>
          )}

          {Boolean(externalStatusTags.length) && (
            <FeedCardMargin top right bottom left>
              <FeedCardTags tags={externalStatusTags} variant={FeedCardTagsVariant.BADGE} />
            </FeedCardMargin>
          )}

          {showCardComment && (
            <FeedCardMargin top right bottom left>
              <div className={classes['card-wrapper__headline']}>{headline}</div>
            </FeedCardMargin>
          )}

          <div
            className={classes['card-wrapper__content']}
            style={{ ...(showCardComment && { backgroundColor }) }}
          >
            <div
              className={classNames(classes['card-wrapper__child'], {
                [classes['card-wrapper__child--clickable']]: clickableChild,
              })}
              onClick={() => clickableChild && childClickHandler()}
            >
              {clickableChild && <CardOpenButton label={getStoryCardName({ card, t })} />}

              {children}
            </div>

            {Boolean(externalTags.length) && (
              <FeedCardMargin right left>
                <FeedCardTags tags={externalTags} />
              </FeedCardMargin>
            )}

            {authorOrPosted && (
              <FeedCardMargin right left>
                <div className={classes['card-wrapper__author-time']}>
                  {contentAuthor && (
                    <div className={classes['card-wrapper__content-author']}>
                      {`${t('common.by')} ${contentAuthor}`}
                    </div>
                  )}
                  {contentAuthor && showPostedTime && <span>•</span>}
                  {showPostedTime && (
                    <div className={classes['card-wrapper__posted-time']}>{date}</div>
                  )}
                </div>
              </FeedCardMargin>
            )}

            {authorTimeSyncComponent}

            {!chatId && renderReactionsCounter && (
              <FeedCardMargin right left>
                <div className={classes['card-wrapper__reactions-counter']}>
                  <ReactionsCounter
                    totalCount={totalReactionsCount}
                    reactions={reactions}
                    myReaction={myReaction}
                  />
                </div>
              </FeedCardMargin>
            )}

            {!chatId && (
              <FeedCardMargin right left>
                <div
                  className={classNames(classes['card-wrapper__action-pane'], {
                    [classes['card-wrapper__action-pane--mobile']]: isMobileOnly,
                  })}
                >
                  <ActionPane
                    card={card}
                    myReaction={myReaction}
                    commentsCount={commentsCount}
                    commentsCounterClick={commentsCounterClick}
                    isProfileFeedCard={isProfileFeedCard}
                  />

                  <CardActionsComponent card={card} isProfileFeedCard={isProfileFeedCard} />
                </div>
              </FeedCardMargin>
            )}

            {!chatId && allowComment && (
              <FeedCardMargin right left>
                <Comments
                  storyId={storyId}
                  cardId={cardId}
                  comments={comments}
                  commentsRootCount={commentsRootCount}
                  loadMore={cardCommentDetails}
                />
              </FeedCardMargin>
            )}
          </div>
        </div>

        {freeLoginModal}
      </>
    );
  }
);
